import React, { useEffect, useState } from "react";

// material-ui

import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  Stack,
  TextField,
  useTheme,
  Autocomplete,
} from "@mui/material";

// third-party
import _ from "lodash";
import * as Yup from "yup";
import { useFormik, Form, FormikProvider } from "formik";

// project imports
import CircularWithPath from "../../../../../components/@extended/progress/CircularWithPath";

import { openSnackbar } from "../../../../../api/snackbar";

// assets

// types
import { SnackbarProps } from "../../../../../types/snackbar";
import { FormattedMessage, useIntl } from "react-intl";
import { USERAXIOS } from "../../../../../api/userAxios";
import ReusableModal from "../../../../../components/ReusableComponent/ReusableModal";
import {
  CompanyProfile,
  CompanyReport,
  ReportCandidate,
} from "../../../../../types/companies-managment/CompanyReport";
import { Resume } from "../../../../../types/companies-managment/companyReply";

// constant
const getInitialValues = (companyReport: CompanyReport | null) => {
  if (companyReport) {
    return {
      resume_id: companyReport.resume_id
        ? companyReport.resume_id.toString()
        : "",
      report_candidate_id: companyReport.report_candidate
        ? companyReport.report_candidate?.id.toString()
        : "",
      company_profile_id: companyReport.company_profile?.id
        ? companyReport.company_profile.id.toString()
        : "",
    };
  } else {
    return {
      report_candidate_id: "",
      resume_id: "",
      company_profile_id: "",
    };
  }
};
// ==============================|| COMPANY COMMENT ADD / EDIT - FORM ||============================== //

const AddEditCompanyReport = ({
  open,
  companyReport,
  id,
  loading,
  fetchData,
  setLoading,
  handleClose,
  setResult,
}: {
  open: boolean;
  loading: boolean;
  id: string;
  setLoading: any;
  fetchData: any;
  companyReport: CompanyReport | null;
  handleClose: () => void;
  setResult: any;
}) => {
  const intl = useIntl();
  const [reportCandidates, setReportCandidates] = useState<ReportCandidate[]>(
    []
  );
  const [resumes, setResumes] = useState<Resume[]>([]);
  const [resumeLoading, setResumeLoading] = useState(false);
  const [companyProfiles, setCompanyProfiles] = useState<CompanyProfile[]>([]);
  const theme = useTheme();

  useEffect(() => {
    setLoading(false);
    fetchResumes();
    fetchreportCandidates();
    fetchcompanyProfiles();
  }, []);

  const fetchResumes = async () => {
    setResumeLoading(true);
    try {
      const response = await USERAXIOS.get(`/resumes`);
      setResumes(response.data.data);
    } catch (error) {
      setResumes([]);
    }
    setResumeLoading(false);
  };

  const fetchreportCandidates = async () => {
    try {
      const response = await USERAXIOS.get(`/report_candidates?page=1`);
      setReportCandidates(response.data.data);
    } catch (error) {
      console.error("Error fetching resume packages:", error);
    }
  };

  const fetchcompanyProfiles = async () => {
    try {
      const response = await USERAXIOS.get(`/company_profiles?page=1`);
      setCompanyProfiles(response.data.data);
    } catch (error) {
      console.error("Error fetching reply applicants:", error);
    }
  };

  const companyReportSchema = Yup.object().shape({
    resume_id: Yup.string().required("Resume is required"),
    report_candidate_id: Yup.string().required("Report candidate is required"),
    company_profile_id: Yup.string().required("Reply applicant is required"),
  });

  const formik = useFormik({
    initialValues: getInitialValues(companyReport!),
    validationSchema: companyReportSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const companyReportData: CompanyReport = {
          resume_id: Number(values.resume_id),
          report_candidate_id: Number(values.report_candidate_id),
          company_profile_id: Number(values.company_profile_id),
        };

        if (companyReport) {
          const response = await USERAXIOS.post(
            `/company_reports/${companyReport?.id}`,
            companyReportData
          );
          const updatedcompanyReport = response.data.data;

          if (updatedcompanyReport) {
            setResult((prev) =>
              prev.map((report) =>
                report.id === updatedcompanyReport.id
                  ? updatedcompanyReport
                  : report
              )
            );

            openSnackbar({
              open: true,
              message: "Company Report Edited Successfully",
              anchorOrigin: { vertical: "top", horizontal: "right" },
              variant: "alert",
              alert: { color: "success" },
            } as SnackbarProps);
          }
        } else {
          const response = await USERAXIOS.post(
            `/company_reports`,
            companyReportData
          );

          const newcompanyReport = response.data.data;

          if (newcompanyReport) {
            setResult((prev) => [...prev, newcompanyReport]);

            openSnackbar({
              open: true,
              message: "Company Report Added Successfully!",
              anchorOrigin: { vertical: "top", horizontal: "right" },
              variant: "alert",
              alert: { color: "success" },
            } as SnackbarProps);
          }
        }

        setSubmitting(false);
        handleClose();
      } catch (error) {
        console.error("Error submitting form:", error);
        setSubmitting(false);
      }
    },
  });

  const { handleSubmit, isSubmitting } = formik;

  if (loading)
    return (
      <Box sx={{ p: 5 }}>
        <Stack direction="row" justifyContent="center">
          <CircularWithPath />
        </Stack>
      </Box>
    );

  return (
    <ReusableModal open={open} handleClose={handleClose}>
      <FormikProvider value={formik}>
        <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
          <DialogTitle
            color="primary"
            style={{
              textAlign: "center",
              fontSize: "22px",
              fontWeight: "bold",
              marginBottom: "10px",
            }}
          >
            {companyReport ? (
              <FormattedMessage id="Edit Company Report" />
            ) : (
              <FormattedMessage id="New Company Report" />
            )}
          </DialogTitle>
          <Divider />
          <DialogContent sx={{ p: 2.5 }}>
            <Grid container>
              <Grid item xs={12} md={12} mb={1}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="resume--autocomplete">
                    <FormattedMessage id="Resume " />{" "}
                    <span style={{ color: "red" }}>*</span>
                  </InputLabel>
                  <FormControl
                    fullWidth
                    error={Boolean(
                      formik.touched.resume_id && formik.errors.resume_id
                    )}
                  >
                    <Autocomplete
                      id="resume--autocomplete"
                      options={resumes}
                      getOptionLabel={
                        (option) => option.objectives || ""
                        //   {
                        //   return theme.direction === "ltr"
                        //     ? option.note
                        //     : option?.note_trans;
                        // }
                      }
                      value={
                        resumes.find(
                          (pkg) => pkg.id.toString() === formik.values.resume_id
                        ) || null
                      }
                      onChange={(_, value) => {
                        formik.setFieldValue(
                          "resume_id",
                          value ? value.id.toString() : ""
                        );
                      }}
                      // onInputChange={(_, value) => {
                      //   setResumeInput(value);
                      // }}
                      loading={resumeLoading}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          error={Boolean(
                            formik.touched.resume_id && formik.errors.resume_id
                          )}
                          helperText={
                            typeof formik.errors.resume_id === "string" &&
                            formik.touched.resume_id
                              ? formik.errors.resume_id
                              : undefined
                          }
                          placeholder="Select a resume "
                        />
                      )}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                    />
                  </FormControl>
                </Stack>
              </Grid>

              <Grid item xs={12} md={12} mb={1}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="report-candidate-autocomplete">
                    <FormattedMessage id="Report candidate" />{" "}
                    <span style={{ color: "red" }}>*</span>
                  </InputLabel>
                  <FormControl
                    fullWidth
                    error={Boolean(
                      formik.touched.report_candidate_id &&
                        formik.errors.report_candidate_id
                    )}
                  >
                    <Autocomplete
                      id="report-candidate-autocomplete"
                      options={reportCandidates}
                      getOptionLabel={(option) => {
                        return theme.direction === "ltr"
                          ? option.text
                          : option?.text_trans?.ar;
                      }}
                      value={
                        reportCandidates.find(
                          (pkg) => pkg.id == formik.values.report_candidate_id
                        ) || null
                      }
                      onChange={(_, value) => {
                        formik.setFieldValue(
                          "report_candidate_id",
                          value ? value.id : ""
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          error={Boolean(
                            formik.touched.report_candidate_id &&
                              formik.errors.report_candidate_id
                          )}
                          helperText={
                            typeof formik.errors.report_candidate_id ===
                              "string" && formik.touched.report_candidate_id
                              ? formik.errors.report_candidate_id
                              : undefined
                          }
                          placeholder="Select a vacancy "
                        />
                      )}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                    />
                  </FormControl>
                </Stack>
              </Grid>

              <Grid item xs={12} md={12} mb={1}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="company_profile-autocomplete">
                    <FormattedMessage id="Company profile" />{" "}
                    <span style={{ color: "red" }}>*</span>
                  </InputLabel>
                  <FormControl
                    fullWidth
                    error={Boolean(
                      formik.touched.company_profile_id &&
                        formik.errors.company_profile_id
                    )}
                  >
                    <Autocomplete
                      id="company_profile-autocomplete"
                      options={companyProfiles}
                      getOptionLabel={(option) => {
                        return theme.direction === "ltr"
                          ? option.company_name
                          : option?.company_name_translations?.ar;
                      }}
                      value={
                        companyProfiles.find(
                          (app) =>
                            app.id.toString() ===
                            formik.values.company_profile_id
                        ) || null
                      }
                      onChange={(_, value) => {
                        formik.setFieldValue(
                          "company_profile_id",
                          value ? value.id.toString() : ""
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          error={Boolean(
                            formik.touched.company_profile_id &&
                              formik.errors.company_profile_id
                          )}
                          helperText={
                            typeof formik.errors.company_profile_id ===
                              "string" && formik.touched.company_profile_id
                              ? formik.errors.company_profile_id
                              : undefined
                          }
                          placeholder="Select a reply applicant"
                        />
                      )}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                    />
                  </FormControl>
                </Stack>
              </Grid>
            </Grid>
          </DialogContent>
          <Divider />
          <DialogActions sx={{ p: 2.5 }}>
            <Grid container justifyContent="center" alignItems="center">
              <Stack direction="row" spacing={2} sx={{ width: 1 }}>
                <Grid item md={2} />
                <Grid item md={4}>
                  <Button
                    disabled={isSubmitting}
                    fullWidth
                    type="submit"
                    variant="contained"
                  >
                    <FormattedMessage
                      id={companyReport ? "Update" : "Submit"}
                    />
                  </Button>
                </Grid>
                <Grid item md={4}>
                  <Button
                    fullWidth
                    color="secondary"
                    variant="outlined"
                    onClick={handleClose}
                  >
                    <FormattedMessage id="Cancel" />
                  </Button>
                </Grid>
                <Grid item md={2} />
              </Stack>
            </Grid>
          </DialogActions>
        </Form>
      </FormikProvider>
    </ReusableModal>
  );
};

export default AddEditCompanyReport;
